/**
 * Cross-cutting UI state that the top bar and the page below it both touch —
 * the prototype held these in React contexts wrapped around the whole shell
 * (ReportsContext.dialog, SettingsContext.dirty/revision).
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type AppDialog =
  'create-report' | 'campaign' | 'add-widget' | 'export-report' | 'share-report' | 'invite-member';

export type UiState = {
  dialog: AppDialog | null;
  settings: {
    /** A field on the current settings tab differs from its saved value. */
    dirty: boolean;
    /** Bumped by Cancel: the tab remounts from saved values. */
    revision: number;
    /** Bumped by Save: the tab commits its form. */
    saveRequest: number;
  };
};

const initialState: UiState = {
  dialog: null,
  settings: { dirty: false, revision: 0, saveRequest: 0 },
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openDialog(state, action: PayloadAction<AppDialog>) {
      state.dialog = action.payload;
    },
    closeDialog(state) {
      state.dialog = null;
    },
    setSettingsDirty(state, action: PayloadAction<boolean>) {
      state.settings.dirty = action.payload;
    },
    cancelSettings(state) {
      state.settings.dirty = false;
      state.settings.revision += 1;
    },
    requestSettingsSave(state) {
      state.settings.saveRequest += 1;
    },
  },
});

export const { openDialog, closeDialog, setSettingsDirty, cancelSettings, requestSettingsSave } =
  uiSlice.actions;
